import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { GenericError, JsonFailure, RequestStatus } from '@skynet/data-ws-core';
import { registerErrorMessage, parseResponse } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';

import { addRetainerProject } from 'src/store/retainers/terms/actions';

import type { JsonProject, JsonProjectResponse } from './types';
import { ProjectCommands } from './types';
import { EMPTY_ARRAY } from 'src/constants';

export interface RetainersProjectsState {
  requestStatus: RequestStatus;
  error: null | GenericError;

  list: JsonProject[];
  companies: string[];
  sortKey: string;
  searchInput: string;
  hideEmptyAccounts: boolean;
}

const allowedCommands = Object.values(ProjectCommands);

registerErrorMessage(ProjectCommands.Projects, {
  title: 'Retainers Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

export const initialState: RetainersProjectsState = {
  requestStatus: null,
  error: null,

  list: [],
  companies: EMPTY_ARRAY,
  // search and sort

  searchInput: '',
  sortKey: 'name_asc',
  hideEmptyAccounts: false,
};

const retainersProjectsSlice = createSlice({
  name: 'projects', // retainers.projects
  initialState,
  reducers: {
    setSearchInput(state, action: PayloadAction<string>) {
      state.searchInput = action.payload;
    },
    setSortKey(state, action: PayloadAction<string>) {
      state.sortKey = action.payload;
    },
    setHideEmptyAccounts(state, action: PayloadAction<boolean>) {
      state.hideEmptyAccounts = action.payload;
    },
  },
  extraReducers: (builder) => {
    // A brand new project has no accounts yet, so the "Hide empty projects"
    // filter would swallow it as soon as the refreshed `Projects` list arrives.
    // Turn the filter off so the project the user just created stays visible in
    // the sidebar (mirrors `addLoan` in the loans slice).
    builder.addCase(addRetainerProject, (state) => {
      state.hideEmptyAccounts = false;
    });

    // on WS message

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error } = parseResponse<JsonProjectResponse>(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      console.debug(
        `retainers/projects/processing ${actionOnMessage.toString()}`,
        action,
        allowedCommands,
      );

      const { command } = json;

      if (error) {
        state.error = error;
        return;
      }

      if (command === ProjectCommands.Projects) {
        state.list = json.list.map((item) => ({
          ...item,
          name: item.name ?? 'N/A',
        }));
        state.companies = json.companies || EMPTY_ARRAY;
        state.requestStatus = 'success';
        return;
      }
    });
  },
});

export const retainersProjectsActions = { ...retainersProjectsSlice.actions };

export default retainersProjectsSlice.reducer;
